import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import SetupAuthenticator from './SetupAuthenticator'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import { useCognito, useCognitoDispatch } from '../../CognitoContextProvider'
import { mfaTypes } from 'components/Cognito/types'
import PortalModal from 'components/Shared/PortalModal'

import { ConnectedIcon } from 'images'

export default function SetupMFA({
  authAppEnabled = true,
  emailEnabled = true,
  authBtnText = null,
  emailBtnText = null,
  removeEnabled = false,
  onAuthenticatorSetupSuccess = () => {},
}) {
  const { t } = useTranslation()
  const location = useLocation()
  const { softwareTokenSetupOpen } = location.state || {}
  const [authAppModalOpen, setAuthAppModalOpen] = useState(softwareTokenSetupOpen || false)
  const {
    user: { mfaSettings },
    error,
    loading,
  } = useCognito()
  const { activate_mfa = [] } = mfaSettings || {}
  const { removeSoftwareToken, setupEmailOTP, promoteTemporarySession } = useCognitoDispatch()
  const [removePromptOpen, setRemovePromptOpen] = useState(false)
  const [removeError, setRemoveError] = useState(false)
  const hasSetupAuthenticator = activate_mfa.some((x) => x === mfaTypes.softwareTokenMfa)
  const [emailSetupSuccess, setEmailSetupSuccess] = useState(false)

  const closeRemovePrompt = () => setRemovePromptOpen(false)

  useEffect(() => {
    let timer
    if (emailSetupSuccess) {
      timer = setTimeout(() => {
        promoteTemporarySession()
        setEmailSetupSuccess(false)
      }, 3000)
    }

    return () => {
      clearTimeout(timer)
    }
  }, [emailSetupSuccess, promoteTemporarySession])

  if (emailSetupSuccess) {
    return (
      <div className="setup-mfa">
        <div className="setup-mfa_content d-flex flex-column align-items-center gap-3">
          <div className="setup-mfa__method-email-container">
            <span className="setup-mfa__method_verified">
              <ConnectedIcon width={10} height={10} />
              {t('SetupMFA.email.success')}
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="setup-mfa">
      <div className="setup-mfa_content d-flex flex-column align-items-center gap-3">
        {error && <p className="alert alert-danger">{t(error)}</p>}
        {authAppEnabled && (!hasSetupAuthenticator || !removeEnabled) && (
          <SecondaryButton
            type="submit"
            color="navy"
            onClick={() => setAuthAppModalOpen(true)}
            classNames="setup-mfa__method-btn"
            text={authBtnText || t('SetupMFA.authenticatorAppBtn')}
            fullWidth={false}
            disabled={loading || emailSetupSuccess}
            disabledTitle=""
          />
        )}
        {hasSetupAuthenticator && removeEnabled && (
          <SecondaryButton
            color="navy"
            classNames="setup-mfa__method-btn"
            text={t('common.remove')}
            onClick={() => {
              setRemovePromptOpen(true)
            }}
          />
        )}
        {emailEnabled && (
          <>
            <span className="setup-mfa__method-text">{t('common.or')}</span>
            <SecondaryButton
              type="submit"
              color="navy"
              onClick={() =>
                setupEmailOTP(true, ({ success }) => {
                  setEmailSetupSuccess(success)
                })
              }
              classNames="setup-mfa__method-btn email-btn"
              text={emailBtnText || t('SetupMFA.emailBtn')}
              fullWidth={false}
              disabled={loading || emailSetupSuccess}
              disabledTitle=""
            />
          </>
        )}
      </div>
      <AuthenticatorAppModal
        isOpen={authAppModalOpen}
        onSuccess={onAuthenticatorSetupSuccess}
        setOpen={setAuthAppModalOpen}
      />
      <PortalModal isOpen={removePromptOpen} onClickOutside={closeRemovePrompt}>
        <div className="content compact text-center" style={{ maxWidth: '420px' }}>
          <h1 className="autofill_warning-header">{t('SetupMFA.removeAuthenticator.title')}</h1>
          <p className="autofill_warning-msg">{t('SetupMFA.removeAuthenticator.description')}</p>
          {removeError && error && <p className="my-3 warning-red">{t(error)}</p>}
          <div className="footer">
            <TransparentBgButton
              text={t('SetupMFA.removeAuthenticator.dontRemove')}
              onClick={closeRemovePrompt}
              color="navy"
              disabled={loading}
            />
            <SecondaryButton
              color="orange"
              text={t('SetupMFA.removeAuthenticator.remove')}
              onClick={() =>
                removeSoftwareToken(({ success }) => {
                  if (success) {
                    setRemovePromptOpen(false)
                  } else {
                    setRemoveError(true)
                  }
                })
              }
              type="button"
              disabledTitle=""
              disabled={loading}
            />
          </div>
        </div>
      </PortalModal>
    </div>
  )
}

SetupMFA.propTypes = {
  authAppEnabled: PropTypes.bool,
  emailEnabled: PropTypes.bool,
  authBtnText: PropTypes.string,
  emailBtnText: PropTypes.string,
  removeEnabled: PropTypes.bool,
  onAuthenticatorSetupSuccess: PropTypes.func,
}

function AuthenticatorAppModal({ isOpen, setOpen, onSuccess }) {
  if (!isOpen) return null

  return (
    <SetupAuthenticator
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      onSuccess={() => {
        if (onSuccess) onSuccess()
        setOpen(false)
      }}
    />
  )
}

AuthenticatorAppModal.propTypes = {
  isOpen: PropTypes.bool,
  setOpen: PropTypes.func,
  onSuccess: PropTypes.func,
}
